// FILE: src/components/QuestionPerformance.js
//
// Admin → Question Performance view (Phase 1).
//
// Loads question-level metrics from the `question_performance_summary` view
// (migration 011), joins them with the in-memory question store (so we can
// show a short question preview + reference id), and renders a sortable
// table of per-question performance indicators.
//
// This is deliberately pragmatic: no charts, no filters beyond exam tabs,
// and no write actions.  It exists so Bryan can see the shape of the data
// flowing in before Phase 2 (experimental questions) and Phase 3 (question
// health intelligence) are built on top.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { loadQuestionPerformance } from '../../services/questionAttemptsService';
import { ensureCertLoaded } from '../../services/certBankLoader';
import { getAllExams } from '../../config/exams';

// ── Styles ───────────────────────────────────────────────────────────────────

const S = {
  wrapper: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#1a1a2e',
    color: '#fff',
    padding: '14px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    flex: 1,
    letterSpacing: '0.5px',
  },
  refreshButton: {
    padding: '5px 12px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.78rem',
  },
  body: {
    flex: 1,
    padding: '24px 32px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
  },
  tab: (active) => ({
    padding: '8px 20px',
    background: active ? '#1565c0' : '#fff',
    color: active ? '#fff' : '#444',
    border: `1px solid ${active ? '#1565c0' : '#ddd'}`,
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: active ? '600' : '400',
  }),
  card: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  emptyState: {
    padding: '48px 24px',
    textAlign: 'center',
    color: '#666',
    fontSize: '0.95rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.88rem',
  },
  th: (sortable) => ({
    padding: '10px 12px',
    textAlign: 'left',
    background: '#f7f7f9',
    borderBottom: '1px solid #e0e0e0',
    color: '#444',
    fontWeight: '600',
    cursor: sortable ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  }),
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #f0f0f0',
    color: '#222',
    verticalAlign: 'top',
  },
  tdNumeric: {
    padding: '10px 12px',
    borderBottom: '1px solid #f0f0f0',
    color: '#222',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  refId: {
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.78rem',
    color: '#555',
    background: '#f0f0f0',
    border: '1px solid #e0e0e0',
    borderRadius: '3px',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  },
  preview: {
    color: '#333',
    display: 'block',
    marginTop: '4px',
    maxWidth: '520px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sortArrow: {
    marginLeft: '4px',
    color: '#999',
    fontSize: '0.7rem',
  },
  footer: {
    marginTop: '14px',
    fontSize: '0.78rem',
    color: '#777',
  },
};

// ── Column definitions ──────────────────────────────────────────────────────
//
// `sortKey` is the property name on the enriched metric object the table sorts
// by; `align` controls the cell alignment class.  Keeping this declarative
// makes it trivial for Phase 3 to add more columns (difficulty, discrimination,
// badges) without surgery on the render function.

const COLUMNS = [
  { key: 'referenceId',    label: 'Question',      sortKey: 'referenceId',    align: 'left'   },
  { key: 'attempts',       label: 'Attempts',      sortKey: 'attempts',       align: 'right'  },
  { key: 'correctRate',    label: 'Correct %',     sortKey: 'correctRate',    align: 'right'  },
  { key: 'avgTime',        label: 'Avg Time',      sortKey: 'avgTimeSeconds', align: 'right'  },
  { key: 'skipRate',       label: 'Skip %',        sortKey: 'skipRate',       align: 'right'  },
  { key: 'flagRate',       label: 'Flag %',        sortKey: 'flagRate',       align: 'right'  },
  { key: 'lastAttempted',  label: 'Last Seen',     sortKey: 'lastAttemptedAt', align: 'right' },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatPct(rate) {
  if (rate == null || Number.isNaN(rate)) return '—';
  return `${Math.round(rate * 100)}%`;
}

function formatSeconds(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function formatRelative(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1)  return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)   return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30)    return `${days}d ago`;
  return date.toLocaleDateString();
}

// Builds a preview lookup for ONE cert's pool: { [String(id)]: { referenceId, preview } }
function buildLookupFromPool(pool) {
  const lookup = {};
  if (!Array.isArray(pool)) return lookup;
  for (const q of pool) {
    const preview = (q.question || '').slice(0, 140);
    lookup[String(q.id)] = {
      referenceId: q.referenceId || String(q.id),
      preview,
      isExperimental: q.isExperimental === true,
    };
  }
  return lookup;
}

// Joins metrics with the question preview lookup.  Rows whose question_id is
// no longer present in the store still render (with a placeholder) so
// historical attempts are never hidden.
function enrich(metrics, lookup) {
  return metrics.map((m) => {
    const hit = lookup[String(m.questionId)] || null;
    return {
      ...m,
      referenceId:    hit?.referenceId    || m.questionId,
      preview:        hit?.preview        || '(question no longer in bank)',
      isExperimental: hit?.isExperimental === true,
    };
  });
}

// Stable comparator factory.  Numeric + string columns get their natural
// sort; undefined / null are pushed to the bottom regardless of direction
// so "no data" rows don't hijack the top of the sorted list.
function compareBy(sortKey, direction) {
  const dir = direction === 'asc' ? 1 : -1;
  return (a, b) => {
    const va = a[sortKey];
    const vb = b[sortKey];

    const aMissing = va == null;
    const bMissing = vb == null;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;

    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dir;
    }
    return String(va).localeCompare(String(vb)) * dir;
  };
}

// ── Component ───────────────────────────────────────────────────────────────

export default function QuestionPerformance({ onBack, questionStores = null }) {
  const exams = getAllExams();

  const [activeExamId, setActiveExamId] = useState(exams[0]?.id || 'pmp');
  const [metrics, setMetrics]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadedAt, setLoadedAt]         = useState(null);
  const [error, setError]               = useState(null);
  const [sortKey, setSortKey]           = useState('attempts');
  const [sortDir, setSortDir]           = useState('desc');
  // Phase 2 — type filter: 'all' | 'scored' | 'experimental'
  const [typeFilter, setTypeFilter]     = useState('all');

  const fetchMetrics = useCallback(async (examId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await loadQuestionPerformance(examId);
      setMetrics(Array.isArray(data) ? data : []);
      setLoadedAt(new Date());
    } catch (err) {
      console.warn('[QuestionPerformance] load failed:', err);
      setMetrics([]);
      setError(err?.message || 'Failed to load performance data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics(activeExamId);
  }, [activeExamId, fetchMetrics]);

  // Preview lookup for the CURRENTLY VIEWED cert only (PR 2B — lazy loading).
  // The live store is authoritative and used synchronously when populated;
  // otherwise we lazily load just this cert's bank into a fallback state. A
  // failed lookup is non-fatal — enrich() still renders rows with a referenceId
  // placeholder — so the async path stays best-effort.
  const [fallbackLookup, setFallbackLookup] = useState({});
  useEffect(() => {
    const stored = questionStores && questionStores[activeExamId];
    if (Array.isArray(stored)) {
      setFallbackLookup({});            // store is authoritative; no fallback needed
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await ensureCertLoaded(activeExamId);
        if (!cancelled) setFallbackLookup(buildLookupFromPool(result.questions));
      } catch (err) {
        if (!cancelled) console.warn('[QuestionPerformance] preview lookup load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeExamId, questionStores]);

  const questionLookup = useMemo(() => {
    const stored = questionStores && questionStores[activeExamId];
    return Array.isArray(stored) ? buildLookupFromPool(stored) : fallbackLookup;
  }, [activeExamId, questionStores, fallbackLookup]);

  const rows = useMemo(() => {
    const enriched = enrich(metrics, questionLookup);
    const filtered = typeFilter === 'all'
      ? enriched
      : typeFilter === 'experimental'
        ? enriched.filter((r) => r.isExperimental === true)
        : /* scored */ enriched.filter((r) => r.isExperimental !== true);
    return [...filtered].sort(compareBy(sortKey, sortDir));
  }, [metrics, questionLookup, sortKey, sortDir, typeFilter]);

  function handleSort(nextSortKey) {
    if (nextSortKey === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(nextSortKey);
      // Numeric columns default to descending (biggest first); strings to ascending.
      setSortDir(nextSortKey === 'referenceId' ? 'asc' : 'desc');
    }
  }

  const arrow = (colSortKey) =>
    colSortKey === sortKey ? (sortDir === 'asc' ? '▲' : '▼') : '';

  return (
    <div data-testid="question-performance-screen" style={S.wrapper}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={S.header}>
        <button
          data-testid="question-performance-back-button"
          style={S.backButton}
          onClick={onBack}
        >
          ← Back
        </button>
        <span style={S.headerTitle}>📈 Question Performance</span>
        <span style={{ fontSize: '0.8rem', color: '#aaa' }}>
          {loadedAt
            ? `Loaded ${loadedAt.toLocaleTimeString()}`
            : loading
              ? 'Loading…'
              : ''}
        </span>
        <button
          data-testid="question-performance-refresh-button"
          style={S.refreshButton}
          onClick={() => fetchMetrics(activeExamId)}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <div style={S.body}>

        {/* Top-of-card controls — exam tabs + type filter */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            marginBottom: '12px',
          }}
        >
          <div role="tablist" style={S.tabs}>
          {exams.map((exam) => (
            <button
              key={exam.examId}
              data-testid={`question-performance-tab-${exam.examId}`}
              role="tab"
              aria-selected={activeExamId === exam.examId}
              style={S.tab(activeExamId === exam.examId)}
              onClick={() => setActiveExamId(exam.examId)}
            >
              {exam.displayName || exam.examId.toUpperCase()}
            </button>
          ))}
          </div>

          <label
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#444' }}
            htmlFor="question-performance-type-filter"
          >
            Questions:
            <select
              id="question-performance-type-filter"
              data-testid="question-performance-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                padding: '4px 8px',
                fontSize: '0.85rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                background: '#fff',
              }}
            >
              <option value="all">All</option>
              <option value="scored">Scored only</option>
              <option value="experimental">Experimental only</option>
            </select>
          </label>
        </div>

        {/* Table card */}
        <div style={S.card}>
          {error && (
            <div
              data-testid="question-performance-error"
              style={{ ...S.emptyState, color: '#c62828' }}
            >
              {error}
            </div>
          )}

          {!error && loading && metrics.length === 0 && (
            <div style={S.emptyState}>Loading performance data…</div>
          )}

          {!error && !loading && rows.length === 0 && (
            <div
              data-testid="question-performance-empty"
              style={S.emptyState}
            >
              No attempts yet for this exam.  Metrics appear here after learners
              complete sessions.
            </div>
          )}

          {rows.length > 0 && (
            <table data-testid="question-performance-table" style={S.table}>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      data-testid={`col-${col.key}`}
                      style={{
                        ...S.th(true),
                        textAlign: col.align,
                      }}
                      onClick={() => handleSort(col.sortKey)}
                    >
                      {col.label}
                      <span style={S.sortArrow}>{arrow(col.sortKey)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.questionId}
                    data-testid={`question-performance-row-${r.questionId}`}
                  >
                    <td style={S.td}>
                      <span style={S.refId}>
                        {r.referenceId}
                        {r.isExperimental && (
                          <span
                            data-testid={`question-performance-experimental-badge-${r.questionId}`}
                            title="Experimental — shown but not scored"
                            style={{
                              display: 'inline-block',
                              marginLeft: '8px',
                              padding: '1px 6px',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              color: '#5d4037',
                              background: '#fff8e1',
                              border: '1px solid #ffd54f',
                              borderRadius: '3px',
                              verticalAlign: 'middle',
                            }}
                          >
                            EXPERIMENTAL
                          </span>
                        )}
                      </span>
                      <span
                        style={S.preview}
                        title={r.preview}
                      >
                        {r.preview}
                      </span>
                    </td>
                    <td style={S.tdNumeric}>{r.attempts}</td>
                    <td style={S.tdNumeric}>{formatPct(r.correctRate)}</td>
                    <td style={S.tdNumeric}>{formatSeconds(r.avgTimeSeconds)}</td>
                    <td style={S.tdNumeric}>{formatPct(r.skipRate)}</td>
                    <td style={S.tdNumeric}>{formatPct(r.flagRate)}</td>
                    <td style={S.tdNumeric}>{formatRelative(r.lastAttemptedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={S.footer}>
          Data source: <code>question_performance_summary</code> view (source=user only).
          Metrics update as learners complete new sessions.
        </div>
      </div>
    </div>
  );
}
